/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Created by jessenelson on 4/27/15.
 */

function PackageMediaTextAssetSelectorController (
    this: JS5Object,
    _: JS5Object,
    AssetValidationService: JS5Object,
    DynamicPropertiesService: JS5Object,
    ModalService: JS5Object,
    PackageFileUploadService: JS5Object,
    PackageMediaService: JS5Object,
    PackageUtilService: JS5Object,
    TempFileUploadService: JS5Object,
    VALID_FILE_EXTENSIONS: JS5Object,
) {

    const setAssetVirtualPath = (virtualPath: string, isRemoteUploadEnabled: boolean) => {
        this.textAsset.virtualPath = virtualPath;
        this.textAsset.assetId = -1;

        if (isRemoteUploadEnabled) {
            this.textAsset.pendingIngest = true;
        } else {
            delete this.textAsset.pendingIngest;
        }

        PackageUtilService.editorForm.$setDirty();
    };


    //================//
    // Template Logic //
    //================//

    this.remoteUploadEnabled = () => {
        return DynamicPropertiesService.getPropValueAsBoolean('mediaTabRemoteUpload.enabled', false);
    }

    this.hasMainVideo = () => {
        return PackageMediaService.hasMainVideo();
    };

    const canUpload = () => {
        const hasMainVideoMediaInfo: boolean = !!PackageMediaService.getMainVideoMediaInfoXML();
        const ingestHasBegun = PackageMediaService.mainVideoIngestHasStarted();

        return hasMainVideoMediaInfo || ingestHasBegun;
    };

    const canSelectRemote = () => {
        let mainVideoPath = _.get(PackageMediaService.getMainVideo(), 'virtualPath');
        let mainVideoIsUpload = PackageMediaService.isAnUploadPath(mainVideoPath);
        let mainVideoIngestHasCompleted = PackageMediaService.mainVideoIngestHasCompleted();

        return !mainVideoIsUpload || mainVideoIngestHasCompleted;
    };

    this.getDisabledFileSources = () => {
        let newDisabledSources = [];
        if (!canUpload()) {
            newDisabledSources.push('ASPERA');
            newDisabledSources.push('LOCAL');
        }
        if (!canSelectRemote()) {
            newDisabledSources.push('REMOTE');
        }
        if (!_.isEqual(newDisabledSources, this.disabledFileSources)) {
            this.disabledFileSources = newDisabledSources;
        }
        return this.disabledFileSources;
    };

    this.getButtonTooltips = () => {
        let newTooltips: JS5Object = {};
        if (!canUpload()) {
            newTooltips.LOCAL =
                'Local-file ingest of this asset type will be' +
                ' enabled after the main video workflow has' +
                ' started.';
        }
        if (!canSelectRemote()) {
            newTooltips.REMOTE =
                'Network-based ingest of this asset type will be' +
                ' enabled after the main video workflow has' +
                ' completed.';
        }
        if (!_.isEqual(newTooltips, this.buttonTooltips)) {
            this.buttonTooltips = newTooltips;
        }
        return this.buttonTooltips;
    };

    this.onViewAssetButtonClick = ($event: JS5Object) => {
        $event.stopImmediatePropagation();
        PackageMediaService.openViewAssetModal(this.textAsset);
    };

    this.onQualityControlButtonClick = ($event: JS5Object) => {
        $event.stopImmediatePropagation();
        PackageMediaService.openQualityControlModal(this.textAsset);
    };

    this.showAssetWarnings = () => {
        ModalService.showAssetValidationResultsModal(this.textAsset.validationResult, false);
    };

    this.onCurrentFileChanged = () => {
        PackageFileUploadService.unregisterLocalConfig(this.uploaderConfig);
        setAssetVirtualPath(null, false);
        if (_.get(this, 'currentFile.status') === 'PASSED') {
            switch (this.currentFile.type) {
                case 'ASPERA':
                    // TODO: Test in UI when Aspera is configured
                    setAssetVirtualPath(this.currentFile.results[0].uploadedData, true);
                    break;
                case 'LOCAL':
                    // Display fileName (Correct virtualPath is set when media is saved)
                    PackageFileUploadService.registerLocalConfigForUpload(this.uploaderConfig);
                    setAssetVirtualPath(`s3:fakePath/${this.currentFile.files[0].file.name}`, false);
                    break;
                case 'REMOTE':
                    setAssetVirtualPath(this.currentFile.files[0].virtualPath, false);
                    break;
            }
        }
    };


    //========================//
    // Remote File Validation //
    //========================//

    this.getFileExtensions = () => {
        let fileFilterType;
        if (this.variant === 'cc') {
            fileFilterType = 'CC';
        } else {
            fileFilterType = 'SUBTITLE';
        }
        return VALID_FILE_EXTENSIONS[fileFilterType];
    };

    this.validateRemoteFile = (fileSystemItem: JS5Object) => {
        let virtualPath = fileSystemItem.parent + fileSystemItem.name;
        return AssetValidationService.validateTextRemoteMainVideoRemote(
            virtualPath,
            this.textAcceptTemplate,
            PackageMediaService.getMainVideo()
        );
    };


    //========================//
    // Aspera File Validation //
    //========================//

    const validateLocalFile = (localFile: File) => {
        let validationPromise: JS5Promise;
        let mainVideo = PackageMediaService.getMainVideo();

        //
        // TODO:
        //
        // These should be collapsed into a single call, the
        // validation service can figure out whether ingest
        // started or not.
        //

        if (PackageMediaService.mainVideoIngestHasStarted()) {
            validationPromise = AssetValidationService.validateTextLocalMainVideoRemote(
                localFile,
                this.textAcceptTemplate,
                PackageMediaService.getPrimaryAcceptTemplateMember(),
                mainVideo
            );
        } else {
            validationPromise = AssetValidationService.validateTextLocalMainVideoLocal(
                localFile,
                this.textAcceptTemplate,
                PackageMediaService.getPrimaryAcceptTemplateMember(),
                PackageMediaService.getMainVideoMediaInfoXML()
            );
        }
        return validationPromise;
    };

    this.validateAsperaFile = (asperaFileObject: JS5Object) => {
        return validateLocalFile(asperaFileObject.file)
            .then((result: JS5Object) => {
                return PackageFileUploadService.parseAsperaValidationResult(PackageMediaService.packageObject, asperaFileObject, result);
            });
    };


    //=======================//
    // Local File Validation //
    //=======================//

    this.getLocalFilePreValidationErrors = (file: File, filter: JS5Object) => {
        // Doesn't add any local file filters
    };

    this.validateLocalFile = (fileItem: JS5Object, uploaderConfig: JS5Object) => {
        fileItem.onVirtualPathSet = (virtualPath: string) => {
            setAssetVirtualPath(virtualPath, true);
        };
        return validateLocalFile(fileItem._file);
    };


    //=================//
    // Lifecycle Hooks //
    //=================//

    this.$onInit = () => {
        const textTrackRolesProperty = `${this.variant}Roles`;

        this.disabledFileSources = [];
        this.buttonTooltips = {};
        this.componentRoleName = this.componentRole.name;
        this.languages = PackageMediaService.packageMediaData.languages;
        this.textTrackRoles = PackageMediaService.packageMediaData[textTrackRolesProperty];

        this.warnings = {};
        if (_.size(this.textAsset.validationResult)) {
            this.warnings.type = 'warning';
            this.warnings.message = '';
        }
    };

    this.initializeLocalUploaderConfig = (uploaderConfig: JS5Object) => {
        TempFileUploadService.initialize(uploaderConfig);
        this.uploaderConfig = uploaderConfig;
    };

    this.$onChanges = (changes: JS5Object) => {
        if (changes.textAsset && this.uploaderConfig) {
            this.currentFile = null;
        }
    };

    this.$onDestroy = () => {
        PackageFileUploadService.unregisterLocalConfig(this.uploaderConfig);
    };
}

angular.module('prism-ui.package').component('packageMediaTextAssetSelector', {
    controller: PackageMediaTextAssetSelectorController,
    templateUrl: 'package/editor/components/view/media/assetSelector/text/package-media-text-asset-selector-template.html',
    bindings: {
        componentRole: '<',
        listIndex: '<',
        textAcceptTemplate: '<',
        textAsset: '<',
        variant: '<'
    }
});
